import { EvaChart } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import { EvaChartStates } from "types/factory";
import { isNextIndexANumber } from "utils";

import { Format } from "models";

import { createChartLocks } from "../locks";
import { DEFAULT_WIDTH, DEFAULT_Z_INDEX } from "./constants";
import { getGroupNameFromLabel, getTimeSeriesMinMax } from "./utils";

const WELL_COUNT_SERIES_COLOR = "#121212";
const UNFOCUSED_FORECAST_OPACITY = 0.5;

export function translateWellContributionResponseToOptions(
  states: Partial<EvaChartStates>
) {
  const { title, layout, series } = states;
  const filteredSeries = series.filter((s) => s.label.toLowerCase() !== "well count");

  const seriesWithX = filteredSeries.map((s) => ({ ...s, x: layout.xAxis.data }));
  const { xMin, xMax, yMin, yMax } = getTimeSeriesMinMax(seriesWithX, Format.Year);

  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedXMinType,
    lockedXMaxType
  } = createChartLocks({
    type: EvaChart.WellContribution,
    states
  });

  const xAxis: Partial<XAXisOption> = {
    name: layout.xAxis.title,
    type: "time",
    max: lockedXMax,
    min: lockedXMin
  };

  const yAxis: Partial<YAXisOption> = {
    name: layout?.yAxis?.title || "",
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax
  };

  const prepareSeriesData = (x, y, isForecast) => {
    const data = y.map((item, index) => {
      const isSetToZero = isNextIndexANumber(y, index) && !item && !isForecast;
      return [x[index], isSetToZero ? 0 : item];
    });
    return data;
  };

  let options = createEntityState(ChartOption.Chart, {
    chartType: EvaChart.WellContribution,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: title
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        ...yAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: Math.floor(yMin.value),
            type: yMin.type,
            override: true // it manages if lock should be overridden or not
          },
          max: {
            value: Math.floor(yMax.value),
            type: yMax.type,
            override: true // it manages if lock should be overridden or not
          }
        }
      })
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        ...xAxis,
        name: layout.xAxis.title,
        nameGap: 27,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: xMin.value,
            type: lockedXMinType ?? xMin.type,
            format: xMin.format,
            override: true // it manages if lock should be overridden or not
          },
          max: {
            value: xMax.value,
            type: lockedXMaxType ?? xMax.type,
            format: xMax.format,
            override: true // it manages if lock should be overridden or not
          }
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false, // useY is managed at hd chart
        type: "time"
      })
    },
    series: filteredSeries.map((location, index) => {
      const isFacilityCountSeries =
        location.label.toLowerCase() === "facility total" ||
        location.groupTitle.toLowerCase() === "facility";
      const isLicensedGasVolumeSeries =
        location.label.toLowerCase() === "licensed volume";
      const isStacking = !isFacilityCountSeries && !isLicensedGasVolumeSeries;
      const { label, style, y } = location;
      const data = prepareSeriesData(layout.xAxis.data, y, location.isForecast);
      const group = getGroupNameFromLabel(label);
      const id =
        group && group != label ? `${group}, ${label}, ${index}` : `${label}, ${index}`;

      const seriesColor = isFacilityCountSeries
        ? WELL_COUNT_SERIES_COLOR
        : style?.hexColor ?? "#000";
      const lineSeries = createEntityState(ChartOption.LineSeries, {
        id,
        name: label,
        data: data,
        itemStyle: { color: seriesColor },
        lineStyle: {
          width: DEFAULT_WIDTH,
          color: seriesColor,
          type: isLicensedGasVolumeSeries
            ? "dashed"
            : location.isForecast
            ? location.isComparisonForecast
              ? "dotted"
              : "dashed"
            : "solid",
          opacity: 1
        },
        z: DEFAULT_Z_INDEX,
        showSymbol: location.y != null && location.y.length === 1,
        stack: isStacking ? "stack" : "",
        areaStyle: isStacking
          ? location.isForecast
            ? {
                color: seriesColor,
                opacity: UNFOCUSED_FORECAST_OPACITY
              }
            : {
                color: seriesColor
              }
          : null
      });

      return lineSeries;
    })
  });
  if (series.length === 0) {
    options = {};
  }
  return options;
}
